import React from "react";
import { Link } from "react-router-dom";

/**
 * PlyxErrorBoundary — last-line-of-defence for the Plyx surfaces.
 *
 * A naked runtime error in a child (e.g. an unguarded `video.creator.handle`
 * on a partially-loaded record) would otherwise unmount the whole route
 * and leave the user staring at a literal blank black screen. This
 * boundary catches the throw, logs it for the next dev session, and
 * renders a friendly "something snapped — tap to reload" surface so
 * the user can recover without restarting the app.
 *
 * Usage:
 *   <PlyxErrorBoundary scope="feed"><PlyxFeed /></PlyxErrorBoundary>
 *
 * Each instance is scoped (the `scope` prop) so we can identify which
 * surface tripped when reading the console error. Reload via state
 * reset on the boundary itself rather than `window.location.reload()`
 * so the user keeps any SPA state outside the broken branch.
 */
export default class PlyxErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[Plyx:${this.props.scope || "boundary"}]`, error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        data-testid={`plyx-error-${this.props.scope || "boundary"}`}
        className="min-h-[100dvh] flex flex-col items-center justify-center bg-black text-white px-6 text-center"
      >
        <div className="text-5xl mb-3" aria-hidden>⚠️</div>
        <h2 className="text-xl font-semibold mb-1">Something snapped on Plyx.</h2>
        <p className="text-sm text-white/60 max-w-xs mb-6">
          We caught the slip so you don&apos;t see a blank screen. Try this
          panel again, or head back to the feed.
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={this.reset}
            className="px-5 py-2 rounded-full bg-amber-300 text-black font-semibold text-sm hover:bg-amber-200 transition"
            data-testid="plyx-error-retry"
          >
            Try again
          </button>
          <Link
            to="/plyx"
            className="px-5 py-2 rounded-full bg-white/10 border border-white/15 text-sm hover:bg-white/15 transition"
            data-testid="plyx-error-home"
            onClick={this.reset}
          >
            Back to feed
          </Link>
        </div>
      </div>
    );
  }
}
